import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { pdf } from '@react-pdf/renderer';
import Button from '../Button/Button';
import CompteARebourd from '../CompteARebourd/CompteARebourd';
import ValidationPopUp from '../ValidationPopUp/ValidationPopUp';
import FormulaireAdministratif from '../FormulaireAdministratif/FormulaireAdministratif';
import OffrePDF from '../../pages/PDF/OffrePDF';
import CGVAccept from '../CGVAccept/CGVAccept'; // NOUVEAU: Import du composant CGV
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole, isClientRole, isPrestataireRole } from '../../utils/roleUtils';
import './OffreBCI.css';
import { PrintButton } from '../PrintButton/PrintButton';
import { formatPrice, getStatusColor } from '../../utils/entityUtils';
import { getLotImage } from '../../utils/getLotImage';
import OrganismeAutocompleteWithFetch, { Organisme } from '../OrganismeAutocomplete/OrganismeAutocomplete';

interface OffreBCIProps {
  commande: any;
  rang: number;
  viewOnly?: boolean;
  // NOUVEAU: Props pour la gestion des CGV
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  printRef?: any;
}

const OffreBCI: React.FC<OffreBCIProps> = ({
  commande,
  rang = 1 | 2 | 3,
  viewOnly = false,
  acceptCGV = false,
  setAcceptCGV = () => { },
  printRef
}) => {
  const { user } = useAuth();
  const isClientView = isClientRole(user?.role || '');
  const isPrestataireView = isPrestataireRole(user?.role || '');

  // CORRECTION: Tous les useState doivent être déclarés en premier, sans conditions
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [isChooseOrganismeModalOpen, setIsChooseOrganismeModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToAcceptAfterCGV, setReadyToAcceptAfterCGV] = useState(false);
  const [readyToRefuseAfterCGV, setReadyToRefuseAfterCGV] = useState(false);
  const [readyToAcceptOperationAfterCGV, setReadyToAcceptOperationAfterCGV] = useState(false); // Prestataires
  const [readyToAcceptOffreAfterCGV, setReadyToAcceptOffreAfterCGV] = useState(false); // Acheteurs
  const [isAcceptOffreModalOpen, setIsAcceptOffreModalOpen] = useState(false);
  const [isRefuseOffreModalOpen, setIsRefuseOffreModalOpen] = useState(false);
  const [operationData, setOperationData] = useState<any>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // États pour la modal de notification au responsable
  const [isNotifyResponsableModalOpen, setIsNotifyResponsableModalOpen] = useState(false);
  const [responsables, setResponsables] = useState<any[]>([]);
  const [selectedResponsable, setSelectedResponsable] = useState<{ id: number, type: string } | null>(null);
  const [responsablesLoading, setResponsablesLoading] = useState(false);

  // NOUVEAU: Vérifier si une notification a déjà été envoyée à l'acheteur signataire
  // en regardant l'historique de l'opération pour un événement avec prefix "Notification" 
  // et type "BDCI.Operation.{id}"
  const notificationInfo = useMemo(() => {
    const operationId = commande?.id;
    const historique = commande?.historique || [];

    if (!operationId || !Array.isArray(historique)) return { sent: false, labels: [] };

    // Chercher TOUS les événements de notification avec le bon format
    const notificationEvents = historique.filter((event: any) =>
      event.prefix === 'Notification' &&
      event.type === `BDCI.Operation.${operationId}`
    );

    console.log('🔔 OffreBCI - Vérification notifications déjà envoyées:', {
      operationId,
      historiqueLength: historique.length,
      notificationEvents,
      count: notificationEvents.length
    });

    if (notificationEvents.length > 0) {
      // Extraire les labels de toutes les notifications (triées par date décroissante - plus récentes en premier)
      const labels = notificationEvents
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .map((event: any) => event.label)
        .filter((label: string | null) => label !== null);
      return { sent: true, labels };
    }

    return { sent: false, labels: [] };
  }, [commande?.id, commande?.historique]);

  const [organismeValue, setOrganismeValue] = useState("");
  const [selectedOrganisme, setSelectedOrganisme] = useState<Organisme | null>(null);

  // CORRECTION: Calculer le rang effectif basé sur les commandes disponibles
  // Si le rang demandé n'existe pas, utiliser le premier rang disponible
  const effectiveRang = useMemo(() => {
    if (!operationData?.commandes || operationData.commandes.length === 0) {
      return rang;
    }

    // Chercher si une commande existe pour le rang demandé
    const commandeForRang = operationData.commandes.find((cmd: any) => parseInt(cmd.rang) === rang);
    if (commandeForRang) {
      return rang;
    }

    // Sinon, trouver le premier rang disponible (le plus petit)
    const availableRangs = operationData.commandes
      .map((cmd: any) => parseInt(cmd.rang))
      .filter((r: number) => !isNaN(r))
      .sort((a: number, b: number) => a - b);

    if (availableRangs.length > 0) {
      console.log(`Rang ${rang} non trouvé, utilisation du rang ${availableRangs[0]} disponible`);
      return availableRangs[0];
    }

    return rang;
  }, [operationData?.commandes, rang]);

  const handleSubmit = async (email: string) => {
    setError('');
    setMessage('');
    setLoading(true);

    if (!email || email.trim() === '') {
      setError('Veuillez entrer un email.');
      setLoading(false);
      return;
    }

    const isValidEmail = (email: string) => {
      return /^\S+@\S+\.\S+$/.test(email);
    };

    if (!isValidEmail(email)) {
      setError('Veuillez entrer un email valide.');
      setLoading(false);
      return;
    }

    const payload = { email };

    console.log("Données envoyées :", payload);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdci/${commande?.id}/envoyer`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message || 'Commande envoyée avec succès !');
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      } else {
        setError(data.message || 'Une erreur est survenue lors de l\'envoi de la commande');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setError('Une erreur de connexion est survenue');
    } finally {
      setLoading(false);
    }
  };

  // CORRECTION: useEffect après tous les useState
  useEffect(() => {
    const fetchOperationData = async () => {
      try {
        setLoadingData(true);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token manquant');

        let apiUrl = '';
        const currentPath = window.location.pathname;

        if (currentPath.includes('/operation-bci/')) {
          const operationId = currentPath.split('/')[2];
          apiUrl = `/bdci/operation/${operationId}`;
        } else if (currentPath.includes('/bon-commande/')) {
          const commandeId = currentPath.split('/')[2];
          apiUrl = `/bdci/commande/${commandeId}`;
        } else {
          setOperationData(commande);
          setLoadingData(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}${apiUrl}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors du chargement des données');
        }

        const result = await response.json();
        if (result.status === 'success' && result.data) {
          setOperationData(result.data);
        } else {
          throw new Error('Format de réponse invalide');
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
        setOperationData(commande);
      } finally {
        setLoadingData(false);
      }
    };

    fetchOperationData();
  }, [commande, rang]);

  // CORRECTION: Tous les useMemo après les useEffect
  const operationContext = useMemo(() => {
    if (!operationData) return null;

    const status = operationData.status?.nom;
    const statusId = operationData.status?.id;
    const isTransferred = isClientView ?
      operationData.transferred === true :
      operationData.operation?.transferred === true;

    const isPending =
      status === `En attente de l'acceptation du rang ${rang}` ||
      status === 'En consultation' ||
      status === 'En attente de votre acceptation' ||
      status === `En attente de votre acceptation du rang ${rang}` ||
      (isPrestataireView && statusId && (
        statusId === 2 || statusId === 3 || statusId === 4 || statusId === 5
      ));

    return {
      isTransferred,
      status,
      statusId,
      isAccepted: statusId === 7 || status === 'Accepté' || status === 'Validé',
      isRefused: statusId === 8 || status === 'Refusé' || status === 'Refuser',
      isExpired: statusId === 9 || status === 'Expiré' || status === 'Expirer',
      isPending: isPending
    };
  }, [operationData, isClientView, isPrestataireView, rang]);

  const articles = useMemo(() => {
    if (!operationData) return [];

    if (isPrestataireView) {
      return operationData.articles || [];
    }

    if (isClientView) {
      let commandeForRang = operationData.commandes?.find((cmd: any) => parseInt(cmd.rang) === effectiveRang);
      if (!commandeForRang) {
        commandeForRang = operationData.commandes.find((cmd: any) => parseInt(cmd.rang) === (effectiveRang + 1));
      };
      return commandeForRang?.articles || [];
    }

    return [];
  }, [operationData, effectiveRang, isPrestataireView, isClientView]);

  const formattedArticles = useMemo(() => {
    return articles.map((item: any, index: number) => {
      const catalogue = item.catalogue;
      const articleData = item.catalogue?.article;

      if (!catalogue || !articleData) {
        return {
          id: index,
          code: `Article ${index + 1}`,
          nom: 'Article non défini',
          description: '',
          unite: 'Unité',
          quantite: 1,
          prixUnitaire: 0,
          totalHT: 0
        };
      }

      const departementId = operationData?.operation?.lot_departement?.departement?.id || '00';
      const lotId = operationData?.operation?.lot_technique?.id || '000';
      const articleCode = catalogue.code || index;
      const reference = `${departementId.toString().padStart(2, '0')} ${lotId.toString().padStart(3, '0')} ${articleCode} ${effectiveRang}`;

      return {
        id: articleData.id || catalogue.id || index,
        code: reference,
        nom: catalogue.nom || `Article ${index + 1}`,
        description: catalogue.description || '',
        unite: catalogue.unite || 'Unité',
        quantite: parseInt(articleData.quantity || articleData.quantite || 1),
        prixUnitaire: parseFloat(articleData.prix_unitaire || articleData.prix || 0),
        totalHT: parseFloat(articleData.prix_total || articleData.totalHT || 0)
      };
    });
  }, [articles, operationData, effectiveRang]);

  const totalOffre = useMemo(() => {
    return formattedArticles.reduce((sum, article) => sum + article.totalHT, 0);
  }, [formattedArticles]);

  const acheteurRights = useMemo(() => {
    if (!isClientView) return { canAccept: false, canRefuse: false, isSignataire: false };

    const userRole = user?.role || '';
    const isSignataire = userRole === 'Acheteur signataire' ||
      userRole === 'Acheteur signataire admin' ||
      userRole === 'Admin';

    const status = operationData?.status?.nom;
    const canAct = status === 'En attente de votre acceptation' && isSignataire;

    return {
      canAccept: canAct,
      canRefuse: canAct,
      isSignataire: isSignataire
    };
  }, [isClientView, user, operationData]);

  // NOUVEAU: Déterminer si les CGV ont déjà été acceptées
  const cgvAlreadyAccepted = useMemo(() => {
    const isTransferred = operationData?.transferred === true;
    const statusNom = operationData?.status?.nom;

    // Si panier inversé et acheteur, les CGV ont été acceptées lors du lancement de la consultation (tab 1)
    if (isTransferred && isClientView) return true;

    // Si panier normal et status "À analyser", vérifier si un rang précédent a refusé ou expiré
    if (!isTransferred && statusNom === 'À analyser') {
      const rangPrecedent = effectiveRang - 1;
      const commandeRangPrecedent = rangPrecedent > 0
        ? operationData?.commandes?.find((cmd: any) => cmd.rang === rangPrecedent)
        : null;
      // CORRECTION: Inclure les commandes expirées (id_status === 9) en plus des refusées (id_status === 8)
      const rangPrecedentRefuseOuExpire = commandeRangPrecedent?.id_status === 8 || commandeRangPrecedent?.id_status === 9;

      // Si rang précédent a refusé ou expiré, les CGV ont été acceptées lors du lancement de la consultation
      return rangPrecedentRefuseOuExpire;
    }

    return false;
  }, [operationData, effectiveRang, isClientView]);

  // CORRECTION: Tous les useCallback après les useMemo
  const handleAccepterAvecCGV = useCallback(() => {
    // Si les CGV ont déjà été acceptées (rang précédent refusé), ne pas redemander
    if (cgvAlreadyAccepted || acceptCGV) {
      setIsAcceptModalOpen(true);
    } else {
      setReadyToAcceptOperationAfterCGV(true); // CORRECTION: Utiliser l'état pour les prestataires
      setIsCGVModalOpen(true);
    }
  }, [acceptCGV, cgvAlreadyAccepted]);

  // NOUVEAU: Ajouter la fonction manquante handleAccepterOffreAvecCGV
  const handleAccepterOffreAvecCGV = useCallback(() => {
    // Si les CGV ont déjà été acceptées (rang précédent refusé), ne pas redemander
    if (cgvAlreadyAccepted || acceptCGV) {
      setIsAcceptOffreModalOpen(true);
    } else {
      setReadyToAcceptOffreAfterCGV(true); // CORRECTION: Utiliser l'état pour les acheteurs
      setIsCGVModalOpen(true);
    }
  }, [acceptCGV, cgvAlreadyAccepted]);

  // NOUVEAU: Ajouter les fonctions manquantes handleAcceptOffre et handleRefuseOffre
  const handleAcceptOffre = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Détecter si on est côté prestataire ou acheteur
      // Prestataire: operationData est la commande (pas de tableau commandes)
      // Acheteur: operationData est l'opération (avec tableau commandes)
      const isPrestataire = !operationData?.commandes && operationData?.id_operation;

      let url, commandeId;

      if (isPrestataire) {
        // Côté prestataire: route /bdci/commande/<id_commande>/accept
        commandeId = operationData?.id;
        if (!commandeId) {
          throw new Error('ID de la commande non trouvé');
        }
        url = `${process.env.REACT_APP_API_URL}/bdci/commande/${commandeId}/accept`;
      } else {
        // Côté acheteur: route /bdci/operation/<id_operation>/accept/<id_commande>
        const operationId = operationData?.id;
        const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === effectiveRang);
        commandeId = commandeActuelle?.id;

        if (!operationId) {
          throw new Error('ID de l\'opération non trouvé');
        }
        if (!commandeId) {
          throw new Error(`ID de la commande du rang ${effectiveRang} non trouvé`);
        }
        url = `${process.env.REACT_APP_API_URL}/bdci/operation/${operationId}/accept/${commandeId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'acceptation de l\'offre');
      }

      alert('Offre acceptée avec succès !');
      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de l\'acceptation');
    } finally {
      setLoading(false);
    }
  }, [operationData]);

  const handleRefuseOffre = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Détecter si on est côté prestataire ou acheteur
      const isPrestataire = !operationData?.commandes && operationData?.id_operation;

      let url, commandeId;

      if (isPrestataire) {
        // Côté prestataire: route /bdci/commande/<id_commande>/refuse
        commandeId = operationData?.id;
        if (!commandeId) {
          throw new Error('ID de la commande non trouvé');
        }
        url = `${process.env.REACT_APP_API_URL}/bdci/commande/${commandeId}/refuse`;
      } else {
        // Côté acheteur: route /bdci/operation/<id_operation>/refuse/<id_commande>
        const operationId = operationData?.id;
        const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === effectiveRang);
        commandeId = commandeActuelle?.id;

        if (!operationId) {
          throw new Error('ID de l\'opération non trouvé');
        }
        if (!commandeId) {
          throw new Error(`ID de la commande du rang ${effectiveRang} non trouvé`);
        }
        url = `${process.env.REACT_APP_API_URL}/bdci/operation/${operationId}/refuse`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors du refus de l\'offre');
      }

      if (rang < 3) {
        alert(`Offre refusée.`);
      } else {
        alert('Offre refusée.');
      }

      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors du refus');
    } finally {
      setLoading(false);
    }
  }, [operationData, rang]);

  const handleAcceptOperation = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Détecter si on est côté prestataire ou acheteur
      const isPrestataire = !operationData?.commandes && operationData?.id_operation;

      let url, commandeId;

      if (isPrestataire) {
        // Côté prestataire: route /bdci/commande/<id_commande>/accept
        commandeId = operationData?.id;
        if (!commandeId) {
          throw new Error('ID de la commande non trouvé dans les données API');
        }
        url = `${process.env.REACT_APP_API_URL}/bdci/commande/${commandeId}/accept`;
      } else {
        // Côté acheteur: route /bdci/operation/<id_operation>/accept/<id_commande>
        const operationId = operationData?.id;
        const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === effectiveRang);
        commandeId = commandeActuelle?.id;

        if (!operationId) {
          throw new Error('ID de l\'opération non trouvé dans les données API');
        }
        if (!commandeId) {
          console.log('Erreur: operationData', operationData);
          throw new Error(`ID de la commande du rang ${effectiveRang} non trouvé`);
        }
        url = `${process.env.REACT_APP_API_URL}/bdci/operation/${operationId}/accept/${commandeId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'acceptation');
      }

      alert('Opération acceptée avec succès !');
      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de l\'acceptation');
    } finally {
      setLoading(false);
    }
  }, [operationData]);

  const handleRefuseOperation = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Détecter si on est côté prestataire ou acheteur
      const isPrestataire = !operationData?.commandes && operationData?.id_operation;

      let url, commandeId;

      if (isPrestataire) {
        // Côté prestataire: route /bdci/commande/<id_commande>/refuse
        commandeId = operationData?.id;
        if (!commandeId) {
          throw new Error('ID de la commande non trouvé dans les données API');
        }
        url = `${process.env.REACT_APP_API_URL}/bdci/commande/${commandeId}/refuse`;
      } else {
        // Côté acheteur: route /bdci/operation/<id_operation>/refuse/<id_commande>
        const operationId = operationData?.id;
        const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === effectiveRang);
        commandeId = commandeActuelle?.id;

        if (!operationId) {
          throw new Error('ID de l\'opération non trouvé dans les données API');
        }
        if (!commandeId) {
          throw new Error(`ID de la commande du rang ${effectiveRang} non trouvé`);
        }
        url = `${process.env.REACT_APP_API_URL}/bdci/operation/${operationId}/refuse/${commandeId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors du refus');
      }

      if (rang < 3) {
        alert(`Opération refusée.`);
      } else {
        alert('Opération refusée par tous les prestataires.');
      }

      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors du refus');
    } finally {
      setLoading(false);
    }
  }, [operationData, rang]);

  const generatePDF = useCallback(async () => {
    try {
      if (!operationData || !user) {
        console.error('Données manquantes pour générer le PDF');
        alert('Impossible de générer le PDF : données manquantes');
        return;
      }

      const pdfData = {
        ...operationData,
        formulaire: operationData.formulaire || {}
      };

      const blob = await pdf(<OffrePDF commande={pdfData} user={user} />).toBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `offre-${operationData.numero || operationData.id || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  }, [operationData, user]);

  const handleDownloadBonCommande = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      const operationId = operationData?.id;
      if (!operationId) throw new Error("ID de l'opération non trouvé");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/signed-contrat`, {
        method: 'POST', // maintenant en POST pour suivre le modèle Signed
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
        },
        body: JSON.stringify({
          external_id: operationData?.numero?.toString(),
          external_type: 'operation',
          source: 'BCI', // préciser le type pour la route backend
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors du téléchargement du bon de commande');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Retirer le P si le numéro commence par P
      const rawNumero = operationData?.numero ?? operationData?.id?.toString() ?? 'document';
      const numeroSansP = rawNumero.startsWith('P') ? rawNumero.substring(1) : rawNumero;

      const annee = process.env.REACT_APP_ANNEE_EDITION || '25';
      link.download = `${annee}${numeroSansP} (signed).pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement du bon de commande:', error);
      alert('Erreur lors du téléchargement du bon de commande. Veuillez réessayer.');
    }
  }, [operationData]);

  // NOUVEAU: Déterminer l'état spécifique de l'offre pour ce rang
  const offreState = useMemo(() => {
    if (!operationData) {
      return { showBonCommande: false, showRefusMessage: false, refusInfo: null };
    }

    // CORRECTION: Pour les prestataires, utiliser le statut global de l'opération
    if (isPrestataireView) {
      const status = operationData.status?.nom;
      const statusId = operationData.status?.id;

      console.log('=== DEBUG OFFRE STATE PRESTATAIRE ===');
      console.log('Status:', status);
      console.log('Status ID:', statusId);
      console.log('Type compte:', operationData.type_compte);
      console.log('Operation data:', operationData);

      const isAccepted = status === 'Accepté' || status === 'Valider' || status === 'Valide';
      const isRefused = statusId === 8 || status === 'Refusé';

      // CORRECTION: Pour BCI côté prestataire, vérifier le status de la commande dans l'opération parent
      // Si la commande a été refusée et que le prestataire voit cela, c'est forcément l'acheteur qui a refusé
      // car le prestataire ne peut pas refuser sa propre offre une fois envoyée
      let refusedBy = 'acheteur'; // Par défaut côté prestataire BCI, c'est l'acheteur qui refuse

      // NOUVEAU: Vérifier si on a accès à des informations plus précises sur le refus
      // Dans l'architecture BCI, quand un prestataire voit un refus (status 8):
      // - Si c'est lui qui a refusé, il ne devrait plus voir cette page (redirection)
      // - Donc si on affiche cette page avec status refusé, c'est l'acheteur qui a refusé

      console.log('Refusé par:', refusedBy);

      return {
        showBonCommande: isAccepted,
        showRefusMessage: isRefused,
        refusInfo: isRefused ? {
          date: operationData.updated_at,
          prestataireNom: operationData?.contrat?.entite?.nom || operationData?.prestataire?.nom || 'Prestataire',
          organismeNom: operationData?.organisme?.nom || 'Acheteur',
          rang: effectiveRang,
          refusedBy: refusedBy
        } : null
      };
    }

    // CORRECTION: Pour les acheteurs, utiliser la logique par rang existante
    if (isClientView) {
      const commandes = operationData.commandes || [];
      let commandeForRang = commandes.find((cmd: any) => parseInt(cmd.rang) === effectiveRang);

      if (!commandeForRang) {
        commandeForRang = commandes.find((cmd: any) => parseInt(cmd.rang) === (effectiveRang + 1));
      }

      console.log('=== DEBUG OFFRE STATE ACHETEUR ===');
      console.log('Rang:', effectiveRang);
      console.log('Commande pour rang:', commandeForRang);
      console.log('Status ID commande:', commandeForRang?.id_status);

      if (!commandeForRang) {
        return { showBonCommande: false, showRefusMessage: false, refusInfo: null };
      }

      const statusId = commandeForRang.id_status;
      const isAccepted = statusId === 7 || status === 'Accepté' || status === 'Valider' || status === 'Valide'; // Accepté
      const isRefused = statusId === 8 || status === 'Refusé';  // Refusé
      const isExpired = statusId === 9 || status === 'Expiré' || status === 'Expirer';  // Expiré
      const isRefusedOrExpired = isRefused || isExpired;

      // NOUVEAU: Déterminer qui a refusé côté acheteur
      let refusedBy = 'prestataire'; // Par défaut pour l'acheteur, c'est le prestataire qui refuse

      // Si le type_compte dans commandeForRang indique un acheteur, c'est l'acheteur qui a refusé
      if (isRefused && commandeForRang.type_compte) {
        if (commandeForRang.type_compte === 'acheteur' ||
          commandeForRang.type_compte === 'responsable_achat' ||
          commandeForRang.type_compte === 'validateur') {
          refusedBy = 'acheteur';
        }
      }

      return {
        showBonCommande: isAccepted,
        showRefusMessage: isRefusedOrExpired,
        refusInfo: isRefusedOrExpired ? {
          date: commandeForRang.updated_at,
          prestataireNom: commandeForRang.contrat?.entite?.nom || 'Prestataire',
          organismeNom: operationData?.organisme?.nom || 'Acheteur',
          rang: effectiveRang,
          isExpired: isExpired,
          refusedBy: refusedBy
        } : null
      };
    }

    return { showBonCommande: false, showRefusMessage: false, refusInfo: null };
  }, [operationData, effectiveRang, isClientView, isPrestataireView]);

  // CORRECTION: Vérification de chargement APRÈS tous les hooks
  if (loadingData || !operationContext) {
    return <div className="loading-indicator">Chargement de l'offre...</div>;
  }

  // Charger la liste des responsables
  const loadResponsables = async () => {
    setResponsablesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/acheteurs/signataires`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('Responsables chargés:', result);
        setResponsables(result.data || result.signataires || []);
      } else {
        console.error('Erreur lors du chargement des responsables:', response.status);
        setResponsables([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des responsables:', error);
      setResponsables([]);
    } finally {
      setResponsablesLoading(false);
    }
  };

  const handleNotifyResponsable = async () => {
    // Ouvrir la modal de sélection
    setIsNotifyResponsableModalOpen(true);
    loadResponsables();
  };

  const handleConfirmNotifyResponsable = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("Token d'authentification manquant");
      }

      // Vérifier qu'un responsable est sélectionné
      if (!selectedResponsable) {
        alert('Veuillez sélectionner un responsable');
        return;
      }

      // Récupérer l'ID de l'opération depuis l'URL
      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2];

      if (!operationId) {
        throw new Error("ID de l'opération non trouvé");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdci/operation/${operationId}/notification-responsable`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            responsable_id: selectedResponsable.id,
            responsable_type: selectedResponsable.type
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Demande envoyée avec succès:', result);

      alert('Demande envoyée avec succès !');

      // Recharger la page pour afficher la notification dans l'historique
      window.location.reload();

    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande au responsable:', error);
      alert(`Erreur lors de l'envoi de la demande au responsable: ${(error as Error).message}`);
    }
  };

  const getDisplayStatus = () => {
    if (!commande?.status) return 'Statut inconnu';

    const statusName = commande.status.nom;
    const statusId = commande.status.id;

    // Pour les prestataires BCI, adapter l'affichage selon le contexte
    if (isPrestataireView) {
      const rang = getRangPrestataire();

      // Si c'est "En attente de l'acceptation du rang X" et que c'est le rang du prestataire connecté
      if (statusName === `En attente de l'acceptation du rang ${rang}`) {
        return 'À accepter';
      }
      // Sinon, utiliser le nom tel quel
      return statusName;
    }

    // Pour les acheteurs, utiliser le nom tel quel
    return statusName;
  };

  // CORRECTION: Déterminer le rang du prestataire depuis les données API
  const getRangPrestataire = () => {
    if (!commande || !isPrestataireView) return 1;
    return commande.rang || 1;
  };

  // CORRECTION: Fonction pour formater le sous-titre
  const getSousTitre = () => {
    const numero = commande?.operation?.numero || commande?.numero;
    const rang = getRangPrestataire();

    if (isPrestataireView) {
      return `Suivi de la Commande BCI n° ${numero} - Rang ${rang}`;
    }
    return `Suivi de la Commande BCI n° ${numero}`;
  };

  // CORRECTION: Fonction pour formater le subtitle (lot technique + géographique)
  const getSubtitle = () => {
    const lotTechnique = commande?.operation?.lot_technique || commande?.lot_technique;
    const departement = commande?.operation?.lot_departement?.departement || commande?.lot_departement?.departement;

    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };

  return (
    <>
      <div className="consultation-bc" ref={printRef}>
        {/* RÉUTILISATION: Section Descriptif Administratif (même structure que ConsultationBC) */}
        <section>
          <h3 className="section-header">Descriptif Administratif</h3>
          <div className="section-content">
            <FormulaireAdministratif
              formData={{
                ...operationData?.formulaire || {},
                delaisConsultation: operationData?.consultation || 10
              }}
              viewMode={true} // Toujours en lecture seule dans l'offre
              hideBesoin={true} // Masquer le besoin ici, il sera affiché dans la section technique
              errors={{}}
              commande={operationData}
              operation={operationData?.operation || operationData}
            />
          </div>
        </section>

        <div className="page-break" />


        {/* RÉUTILISATION: Section Descriptif Technique (même structure que ConsultationBC) */}
        <section>
          <h3 className="section-header">Descriptif Technique</h3>
          <div className="section-content">
            <div className="form-group">
              <label>Cahier des charges <span className="desc-precision">(Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être proposés par l’acheteur à titre d’information, mais seules les informations écrites ci-dessous sont contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à contrevenir à l’accord-cadre général sans dérogations prévues au contrat est a considérer comme inexistantes dans ce descriptif)</span></label>
              <div className="input-inactif definition-besoin">
                {operationData?.besoin || operationData?.operation?.besoin || operationData?.formulaire?.besoin || 'Non défini'}
              </div>
            </div>
          </div>
        </section>

        <div className="page-break" />


        {/* RÉUTILISATION: Section Descriptif Financier (même structure que ConsultationBC) */}
        <section>
          <h3 className="section-header">Descriptif Financier</h3>
          <div className="section-content">
            {formattedArticles.length > 0 ? (
              <div className="articles-table-container">
                <div className="articles-table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>N° Réf</th>
                        <th>Description de la prestation</th>
                        <th>Unité</th>
                        <th>Quantité</th>
                        <th>Prix unitaire HT</th>
                        <th>Total HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formattedArticles.map((article, index) => (
                        <tr key={article.id || index}>
                          <td>{article.code}</td>
                          <td>
                            <div className="article-name">{article.nom}</div>
                          </td>
                          <td>{article.unite}</td>
                          <td>{article.quantite}</td>
                          <td>{formatPrice(article.prixUnitaire, 4)}</td>
                          <td>{formatPrice(article.totalHT, 4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* RÉUTILISATION: Section total (même classe que ConsultationBC) */}
                <div className="articles-actions">
                  <div className="total-section">
                    <p><strong>Total de l'offre (Rang {effectiveRang}) : {formatPrice(totalOffre, 4)} HT</strong></p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-articles-message">
                <p>Aucun article défini pour cette offre.</p>
              </div>
            )}
          </div>
        </section>

        <div className="page-break" />


        {/* CORRECTION: Section Bon de Commande - Visible pour tous quand accepté */}
        {offreState.showBonCommande && (
          <section className="bon-commande-section">
            <h3 className="section-header">Bon de Commande</h3>
            <div className="section-content">
              <div className="bon-commande-info">
                <div className="bon-commande-message">
                  <p>Le bon de commande a été généré et est disponible au téléchargement.</p>

                  <p>
                    <strong>
                      {isPrestataireView ? (
                        // Message pour les prestataires
                        `Votre offre a été acceptée le ${operationData?.updated_at ?
                          new Date(operationData.updated_at).toLocaleDateString('fr-FR') + ' à ' +
                          new Date(operationData.updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                          : 'date non disponible'
                        }.`
                      ) : (
                        // Message pour les acheteurs (logique existante)
                        `Opération acceptée par ${operationData?.commandes?.find((cmd: any) => parseInt(cmd.rang) === effectiveRang)?.contrat?.entite?.nom ||
                        operationData?.prestataire?.nom ||
                        'le prestataire'
                        } le ${operationData?.commandes?.find((cmd: any) => parseInt(cmd.rang) === effectiveRang)?.updated_at ?
                          new Date(operationData.commandes.find((cmd: any) => parseInt(cmd.rang) === effectiveRang).updated_at).toLocaleDateString('fr-FR') + ' à ' +
                          new Date(operationData.commandes.find((cmd: any) => parseInt(cmd.rang) === effectiveRang).updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                          : 'date non disponible'
                        }.`
                      )}
                    </strong>
                  </p>
                </div>

                {/* Bouton de téléchargement */}
                <div className="bon-commande-actions">
                  <Button
                    value="Télécharger le Bon de Commande"
                    variant="primary"
                    size="medium"
                    icon={<PdfIcon />}
                    onClick={handleDownloadBonCommande}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* NOUVEAU: Section message de refus ou expiration - Visible pour tous quand refusé ou expiré */}
        {offreState.showRefusMessage && offreState.refusInfo && (
          <section className="refus-message-section">
            <div className="section-content message-info">
              <p>
                {isPrestataireView ? (
                  // Message pour les prestataires BCI
                  offreState.refusInfo.isExpired
                    ? `Votre offre a expiré le ${new Date(offreState.refusInfo.date).toLocaleDateString('fr-FR') + ' à ' +
                    new Date(offreState.refusInfo.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                    }.`
                    // CORRECTION: Pour BCI, côté prestataire, si refusé c'est toujours l'acheteur
                    : `Votre offre a été refusée par l'acheteur le ${new Date(offreState.refusInfo.date).toLocaleDateString('fr-FR') + ' à ' +
                    new Date(offreState.refusInfo.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                    }.`
                ) : (
                  // Message pour les acheteurs BCI
                  offreState.refusInfo.isExpired
                    ? `L'offre du prestataire ${offreState.refusInfo.prestataireNom} de rang ${offreState.refusInfo.rang} a expiré le ${new Date(offreState.refusInfo.date).toLocaleDateString('fr-FR') + ' à ' +
                    new Date(offreState.refusInfo.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                    }.`
                    : offreState.refusInfo.refusedBy === 'acheteur'
                      ? `Vous avez refusé cette offre le ${new Date(offreState.refusInfo.date).toLocaleDateString('fr-FR') + ' à ' +
                      new Date(offreState.refusInfo.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                      }.`
                      : `L'offre du prestataire ${offreState.refusInfo.prestataireNom} de rang ${offreState.refusInfo.rang} a été refusée le ${new Date(offreState.refusInfo.date).toLocaleDateString('fr-FR') + ' à ' +
                      new Date(offreState.refusInfo.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                      }.`
                )}
              </p>
            </div>
          </section>
        )}

        {/* CAS 1: Mode normal - Acheteur en attente */}
        {!operationContext.isTransferred && isClientView && operationContext.isPending && (
          <div className="section-content consultation-info">
            <div className="info-header">
              <h4>En attente de l'acceptation du prestataire</h4>
              <p>Le prestataire de rang {effectiveRang} doit accepter ou refuser cette opération.</p>
              {operationData?.expire_at && (
                <p><strong>Échéance dans :</strong> <CompteARebourd targetDate={operationData.expire_at} /></p>
              )}
            </div>
          </div>
        )}

        {/* RÉUTILISATION: Section boutons d'actions (même classe que ConsultationBC) */}
        <div className="action-buttons">
          {/* <Button
          value="PDF de l'offre"
          icon={<PdfIcon />}
          variant="secondary"
          onClick={generatePDF}
          size="small"
        /> */}


          {/* CAS 1: Mode normal - Prestataire doit accepter/refuser l'opération */}
          {!operationContext.isTransferred && isPrestataireView && operationContext.isPending && (
            <div className="action-buttons-group">
              <Button
                value="Refuser l'opération"
                variant="secondary"
                size="small"
                onClick={() => setIsRefuseModalOpen(true)}
                disabled={loading}
              />
              <Button
                value="Accepter l'opération"
                variant="primary"
                size="small"
                onClick={handleAccepterAvecCGV} // NOUVEAU: Utiliser la fonction avec CGV
                disabled={loading}
                title={(!acceptCGV && !cgvAlreadyAccepted) ? "Vous devez accepter les Conditions Générales" : ""}
              />
            </div>
          )}

          {/* CAS 2: Acheteur en attente de décision */}
          {(() => {
            // NOUVEAU: Logique pour gérer panier normal (transferred: false) et panier inversé (transferred: true)
            const statusNom = operationData?.status?.nom;
            const isTransferred = operationData?.transferred === true;

            // CORRECTION: Vérifier explicitement le rôle de l'utilisateur (même logique que OffreBC)
            const userRole = user?.role || '';
            const isSignataire = userRole === 'Acheteur signataire' ||
              userRole === 'Acheteur signataire admin' ||
              userRole === 'Admin';

            // NOUVEAU: Vérifier si c'est un rang avec refus ou expiration précédent (panier normal)
            const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === effectiveRang);
            const rangPrecedent = effectiveRang - 1;
            const commandeRangPrecedent = rangPrecedent > 0
              ? operationData?.commandes?.find((cmd: any) => cmd.rang === rangPrecedent)
              : null;
            // CORRECTION: Inclure les commandes expirées (id_status === 9) en plus des refusées (id_status === 8)
            const rangPrecedentRefuse = commandeRangPrecedent?.id_status === 8 || commandeRangPrecedent?.id_status === 9; // Refusé ou Expiré

            console.log('=== DEBUG AFFICHAGE BOUTONS OFFRE BCI ===');
            console.log('Status:', statusNom);
            console.log('Is transferred:', isTransferred);
            console.log('Rang actuel:', effectiveRang);
            console.log('Rang précédent refusé:', rangPrecedentRefuse);
            console.log('Commande actuelle:', commandeActuelle);
            console.log('User role:', userRole);
            console.log('Is signataire:', isSignataire);

            // NOUVEAU: Conditions d'affichage des boutons:
            // 1. Panier inversé (transferred: true) : status "À accepter"
            // 2. Panier normal (transferred: false) : status "À analyser" ET rang précédent refusé
            const shouldShowButtons = isClientView && (
              (statusNom === 'À accepter') || // Panier inversé
              (!isTransferred && statusNom === 'À analyser' && (effectiveRang === 1 || rangPrecedentRefuse)) // Panier normal
            );

            console.log('Should show buttons:', shouldShowButtons);

            if (!shouldShowButtons) {
              return null;
            }

            // CORRECTION: Si signataire (Acheteur signataire, Acheteur signataire admin ou Admin), afficher les boutons d'action
            if (isSignataire) {
              return (
                <div className="action-buttons-group">
                  <Button
                    value="Refuser l'offre"
                    variant="secondary"
                    size="small"
                    onClick={() => setIsRefuseOffreModalOpen(true)}
                    disabled={loading}
                  />
                  <Button
                    value="Accepter l'offre"
                    variant="primary"
                    size="small"
                    onClick={handleAccepterAvecCGV}
                    disabled={loading}
                  />
                </div>
              );
            } else {
              // NOUVEAU: Acheteur simple peut refuser mais doit notifier pour accepter
              return (
                <>
                  <div className='notifications-section'>
                    <div className="action-buttons-group notify">
                      <Button
                        value="Refuser l'offre"
                        variant="secondary"
                        size="small"
                        onClick={() => setIsRefuseOffreModalOpen(true)}
                        disabled={loading}
                      />
                      <Button
                        value={`Obtenir la validation de l'acheteur signataire`}
                        variant="primary"
                        size="small"
                        onClick={handleNotifyResponsable}
                      />
                    </div>
                    <div className="notification-info-section">
                      {notificationInfo.sent && notificationInfo.labels.length > 0 && (
                        <div className="notifications-sent-list">
                          {notificationInfo.labels.map((label: string, index: number) => (
                            <div key={index} className="notification-sent-info">
                              <span className="notification-sent-icon">✓</span>
                              <span>{label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            }
          })()}

          {/* CAS BDCI Prestataire doit choisir un acheteur */}
          {operationData.status.id === 11 && (
            <Button
              value="Envoyer la proposition à l'acheteur"
              variant='primary'
              size='small'
              disabled={loading}
              onClick={() => setIsChooseOrganismeModalOpen(true)}
            />
          )}

          {/* NOUVEAU: Messages de statut final */}
          {operationContext.isAccepted && (
            <div className="status-message accepted">
              ✓ Offre acceptée
            </div>
          )}

          {operationContext.isRefused && (
            <div className="status-message refused">
              ✗ Offre refusée
            </div>
          )}

          {operationContext.isExpired && (
            <div className="status-message expired">
              ⏰ Offre expirée
            </div>
          )}
        </div>

        {/* Modals */}
        <ValidationPopUp
          isOpen={isAcceptModalOpen}
          title="Accepter l'opération"
          text={`Êtes-vous sûr de vouloir accepter cette opération pour un montant de ${formatPrice(totalOffre, 4)} HT ?`}
          onClose={() => setIsAcceptModalOpen(false)}
          onConfirm={async () => {
            setIsAcceptModalOpen(false);
            await handleAcceptOperation();
          }}
          cancelLabel="Annuler"
          confirmLabel="Accepter"
        />

        <ValidationPopUp
          isOpen={isRefuseModalOpen}
          title="Refuser l'opération"
          text={`Êtes-vous sûr de vouloir refuser cette opération ?`}
          onClose={() => setIsRefuseModalOpen(false)}
          onConfirm={async () => {
            setIsRefuseModalOpen(false);
            await handleRefuseOperation();
          }}
          cancelLabel="Annuler"
          confirmLabel="Refuser"
        />

        <ValidationPopUp
          isOpen={isChooseOrganismeModalOpen}
          title="Envoyer à l'acheteur"
          text="Veuillez choisir l'acheteur à qui envoyer cette proposition"
          onClose={() => { setIsChooseOrganismeModalOpen(false); window.location.reload(); }}
          onConfirm={() => handleSubmit(email)}
          cancelLabel="Annuler"
          confirmLabel="Envoyer"
        >
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          <div className="form-group">
            <label htmlFor="email">Adresse email de l'acheteur</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="acheteur.email@exemple.com"
              required
            />
          </div>
        </ValidationPopUp>

        {/* CORRECTION: Modal CGV avec fermeture automatique */}
        <CGVAccept
          acceptCGV={acceptCGV}
          setAcceptCGV={setAcceptCGV}
          isOpen={isCGVModalOpen}
          onClose={() => {
            setIsCGVModalOpen(false);
            setReadyToAcceptOperationAfterCGV(false);
            setReadyToAcceptOffreAfterCGV(false);
          }}
          onConfirm={() => {
            console.log("=== CGV CONFIRMÉES ===");

            // ✅ Forcer la mise à jour avant de continuer
            setAcceptCGV(true);

            setTimeout(() => {
              setIsCGVModalOpen(false);

              if (readyToAcceptOperationAfterCGV) {
                setReadyToAcceptOperationAfterCGV(false);
                setIsAcceptModalOpen(true);
              }

              if (readyToAcceptOffreAfterCGV) {
                setReadyToAcceptOffreAfterCGV(false);
                setIsAcceptOffreModalOpen(true);
              }
            }, 100);
          }}
        />

        {/* NOUVEAU: Modals pour acceptation/refus offre (acheteurs) */}

        <ValidationPopUp
          isOpen={isAcceptOffreModalOpen}
          title="Accepter l'offre"
          text={`Êtes-vous sûr de vouloir accepter cette offre pour un montant de ${formatPrice(totalOffre, 4)} HT ? Cette action est définitive.`}
          onClose={() => setIsAcceptOffreModalOpen(false)}
          onConfirm={async () => {
            setIsAcceptOffreModalOpen(false);
            await handleAcceptOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Accepter l'offre"
        />

        <ValidationPopUp
          isOpen={isRefuseOffreModalOpen}
          title="Refuser l'offre"
          text={`Êtes-vous sûr de vouloir refuser cette offre ?`}
          // ${rang < 3 ? 'Elle sera automatiquement transmise au prestataire de rang ' + (rang + 1) + '.' : 'Cette action est définitive.'}`}
          onClose={() => setIsRefuseOffreModalOpen(false)}
          onConfirm={async () => {
            setIsRefuseOffreModalOpen(false);
            await handleRefuseOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Refuser l'offre"
        />


      </div>
      {/* Modal notification responsable */}
      <ValidationPopUp
        isOpen={isNotifyResponsableModalOpen}
        title="Obtenir la validation de l'acheteur signataire"
        text=""
        onClose={() => {
          setIsNotifyResponsableModalOpen(false);
          setSelectedResponsable(null);
        }}
        onConfirm={handleConfirmNotifyResponsable}
        cancelLabel="Annuler"
        confirmLabel="Envoyer la demande"
      >
        <div className="mt-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sélectionnez le responsable à qui envoyer la demande :
          </label>
          {responsablesLoading ? (
            <p className="text-gray-500">Chargement...</p>
          ) : responsables.length === 0 ? (
            <p className="text-red-500">Aucun responsable disponible</p>
          ) : (
            <select
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedResponsable ? `${selectedResponsable.type}-${selectedResponsable.id}` : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const [type, id] = value.split('-');
                  setSelectedResponsable({ id: parseInt(id), type });
                } else {
                  setSelectedResponsable(null);
                }
              }}
            >
              <option value="">-- Choisir un responsable --</option>
              {responsables.map((resp: any) => (
                <option
                  key={`${resp.type}-${resp.id}`}
                  value={`${resp.type}-${resp.id}`}
                >
                  {resp.prenom} {resp.nom} - {resp.type === 'responsable_achat' ? 'Acheteur signataire' : 'Validateur Achat'}{resp.fonction ? ` (${resp.fonction})` : ''}
                </option>
              ))}
            </select>
          )}
          {selectedResponsable && (
            <p className="mt-2 text-sm text-gray-600">
              Email : {responsables.find((r: any) => r.id === selectedResponsable.id && r.type === selectedResponsable.type)?.email_contact}
            </p>
          )}
        </div>
      </ValidationPopUp>

      <div className='print-button-container'>
        <PrintButton
          targetRef={printRef}
          fileName={`${commande?.numero || commande?.operation?.numero} - Offre`}
          value="PDF"
          suiviHeaderProps={{
            titre: commande.operation?.nom || commande?.nom,
            sousTitre: getSousTitre(), // fonction disponible dans le parent
            subtitle: getSubtitle(),   // fonction disponible dans le parent
            status: getDisplayStatus(),
            statusColor: getStatusColor(getDisplayStatus()),
            delais: commande.expire_at,
            backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
          }}
        />
      </div>
    </>
  );
};

export default OffreBCI;